import React from 'react'
import Box from '@mui/material/Box'

type Gutter = number | [number, number]

function parseGutter(gutter?: Gutter): [number, number] {
  if (gutter == null) return [0, 0]
  return Array.isArray(gutter) ? gutter : [gutter, gutter]
}

/**
 * MUI `sx` nhân number theo theme spacing (×8).
 * CSSProperties phải vào `style` (px thật), không spread vào `sx`.
 */
export interface RowProps {
  gutter?: Gutter
  style?: React.CSSProperties
  className?: string
  children?: React.ReactNode
  justify?: string
  align?: string
  wrap?: boolean
  [key: string]: any
}

export const Row: React.FC<RowProps> = ({
  gutter,
  style,
  className,
  children,
  justify,
  align,
  wrap = true,
}) => {
  const [gx, gy] = parseGutter(gutter)
  return (
    <Box
      className={className}
      style={{
        marginLeft: gx ? -gx / 2 : undefined,
        marginRight: gx ? -gx / 2 : undefined,
        ...style,
      }}
      sx={{
        display: 'flex',
        flexWrap: wrap ? 'wrap' : 'nowrap',
        rowGap: gy ? `${gy}px` : 0,
        justifyContent:
          justify === 'space-between'
            ? 'space-between'
            : justify === 'center'
              ? 'center'
              : justify === 'end'
                ? 'flex-end'
                : justify,
        alignItems: align,
      }}
    >
      {React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) return child
        if (child.type === Col) {
          return React.cloneElement(child as React.ReactElement<any>, { __gutterX: gx })
        }
        return child
      })}
    </Box>
  )
}

export interface ColProps {
  span?: number
  offset?: number
  style?: React.CSSProperties
  className?: string
  children?: React.ReactNode
  flex?: string | number
  __gutterX?: number
  hidden?: boolean
  [key: string]: any
}

export const Col: React.FC<ColProps> = ({
  span = 24,
  offset = 0,
  style,
  className,
  children,
  flex,
  __gutterX = 0,
  hidden,
}) => {
  if (hidden) return null
  const width = flex != null ? undefined : `${(span / 24) * 100}%`
  return (
    <Box
      className={className}
      style={{
        paddingLeft: __gutterX ? __gutterX / 2 : undefined,
        paddingRight: __gutterX ? __gutterX / 2 : undefined,
        ...style,
      }}
      sx={{
        flex: flex != null ? flex : `0 0 ${width}`,
        width,
        maxWidth: width,
        minWidth: 0,
        marginLeft: offset ? `${(offset / 24) * 100}%` : undefined,
        boxSizing: 'border-box',
      }}
    >
      {children}
    </Box>
  )
}
